import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouteMatch, useHistory } from 'react-router-dom';
import cx from 'classnames';
import { useDispatch } from 'react-redux';
import Icon from 'components/Icon';
import { ProBadge, NewBadge } from 'components/Badge';

import styles from './styles.css';

import { Trans, useTranslation } from 'react-i18next';

const Translation = (title) => {
  const { t } = useTranslation();

  const getValue = (object: Object, value: string) => {
    const transKey = Object.keys(object).find(
      (key) => key === title && object[key]
    );
    return transKey ? object[transKey] : title;
  };

  const translationValues = t('common:sidebar', {
    returnObjects: true,
  }) as Object;

  const translationValue = getValue(translationValues, title);

  return <Trans>{translationValue || title}</Trans>;
};

const Button = ({
  children,
  active,
  onClick,
  hasChildren,
  icon,
  pro,
  isNew,
  dark,
}: any) => {
  return (
    <button
      onClick={onClick}
      className={cx(
        styles.button,
        active && styles.active,
        (hasChildren || dark) && styles.withChildren,
        !hasChildren && styles.withoutChildren
      )}
    >
      {icon && <Icon name={icon} width={24} height={24} />}
      {children}
      {pro && ProBadge}
      {isNew && NewBadge}
      {hasChildren && (
        <Icon
          name={!!active ? 'arrow-up' : 'arrow-down'}
          width={24}
          height={24}
          className={styles.arrow}
        />
      )}
    </button>
  );
};

type ItemProps = {
  items?: [];
  link?: string;
  linkTo?: string;
  title: string;
  icon?: string;
  dark?: boolean;
  onClick?: ({ dispatch }) => void;
  features?: any;
  feature?: string;
  merchant: any;
  condition: any;
  isNew: boolean;
  user: any;
};

const ItemsRenderer = ({
  items,
  link,
  linkTo,
  title,
  condition,
  icon,
  onClick,
  dark,
  feature,
  features,
  merchant,
  isNew,
  user,
}: ItemProps) => {
  const history = useHistory();
  const _match = useRouteMatch(link?.split('#')[0]);
  const dispatch = useDispatch();
  const [isExpanded, setExpanded] = useState(false);
  const hash = (linkTo || link)?.split('#')[1];
  const match =
    !!_match && (!hash ? true : '#' + hash === document.location.hash);

  useEffect(() => {
    setExpanded(!!match);
  }, [!!match]);

  const handleClick = useCallback(() => {
    if (onClick) return onClick({ dispatch });
    history.push(linkTo || link);
  }, []);

  const clickItem = useCallback(() => {
    setExpanded(!isExpanded);
    if (!isExpanded) handleClick();
  }, [isExpanded]);

  const clickSingleItem = useCallback(() => {
    if (!isExpanded) handleClick();
  }, [isExpanded]);

  const hasProBadge = useMemo(() => {
    if (!feature || !features) return false;
    const requiredFeature = features[feature];
    if (
      typeof requiredFeature === 'boolean' ||
      typeof requiredFeature === 'undefined'
    )
      return !requiredFeature;
    return !requiredFeature.enabled;
  }, [feature, features]);

  if (condition && merchant && user && !condition(merchant, user)) return null;

  if (!items)
    return (
      <Button
        onClick={clickSingleItem}
        icon={icon}
        children={Translation(title)}
        dark={dark}
        pro={hasProBadge}
        active={isExpanded}
        isNew={isNew}
      />
    );
  return (
    <>
      <Button
        onClick={clickItem}
        icon={icon}
        children={Translation(title)}
        active={isExpanded}
        pro={hasProBadge}
        isNew={isNew}
        hasChildren
      />
      <ul className={cx(styles.list, !isExpanded && styles.collapsed)}>
        {items.map((props: any) => (
          <li key={props.link}>
            <ItemsRenderer
              {...props}
              features={features}
              merchant={merchant}
              user={user}
            />
          </li>
        ))}
      </ul>
    </>
  );
};

export default ItemsRenderer;
